import { Button, Grid } from '@mui/material';
import { FormInput } from '../../../components/hook-form/form-input.component';
import { ReactHookForm } from '../../../components/hook-form/react-hook-form.component';
import { useSaveData } from '../../../hooks/useSaveData';
import { FileUploaded } from '../../../models/fileUpload';
import { fileUploadApiKeyStore } from '../../../api/file-upload-key-store';
import { useReactHookForm } from '../../../hooks/useReactHookForm';
import { LoadingIndicator } from '../../../components/loading-indicator.component';
import { useDropzone } from 'react-dropzone';
import { useCallback, useEffect, useState } from 'react';
import { FormSelect } from '../../../components/hook-form/form-select.component';
import { useAuthorize } from '../../../hooks/use-authorize';
import { UserPermissionName } from '../../../models/user-permission-name';

const styling = {
  dropZone: {
    padding: '50px',
    border: '0.1em dashed #99A3A4',
    backgroundColor: '#F2F3F4',
    display: 'flex',
    justifyContent: 'center',
    cursor: 'pointer',
    borderRadius: '4px',
  },
  iconButtonStyle: {
    padding: '15px',
  },
  iconStyle: { width: '40px', height: '40px' },
};

const fileTypeOptions = [
  { value: '', label: '' },
  { value: 'Code Report', label: 'Code Report' },
  { value: 'Builder Report', label: 'Builder Report' },
  { value: 'Legacy Report', label: 'Legacy Report' },
  { value: 'Property Not Ready Report', label: 'Property Not Ready Report' },
];

interface DocumentRequestArgs {
  workOrderID?: number;
  tempEntityID?: number;
}

interface UploadFileComponent {
  onClose: () => void;
  fileData?: any;
  onSaveComplete: (data: any) => void;
  documentRequestArgs: DocumentRequestArgs;
  page?: string;
  tabValue?: string;
}

export const UploadFileComponent = ({
  fileData,
  onClose,
  onSaveComplete,
  documentRequestArgs,
  page,
  tabValue,
}: UploadFileComponent) => {
  const [files, setFiles] = useState<any>();
  const [message, setMessage] = useState<any>();
  const form = useReactHookForm({
    defaultValues: {
      ...fileData,
      ...documentRequestArgs,
      fileTypeID:
        fileData?.inspectionReportType === ''
          ? tabValue === 'edit'
            ? 'n/a'
            : ''
          : fileTypeOptions.find((opt) => opt.label === fileData?.inspectionReportType)?.value || '' || '',
    },
  });
  const { isLoading, mutateAsync } = useSaveData<FileUploaded>(
    fileData ? fileUploadApiKeyStore.updateUploadFile() : fileUploadApiKeyStore.uploadFile()
  );

  const handleDrop = useCallback((acceptedFiles: any) => {
    setFiles(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
  });

  const isInspectionReportPermission = useAuthorize([UserPermissionName?.EDIT_INSPECTION_REPORT]);

  const submitHanlder = async (value: any) => {
    if (!value?.fileTypeID && isInspectionReportPermission) {
      setMessage('Please select inspection report type');
    } else {
      setMessage(undefined);
      if (files || fileData) {
        const formData = new FormData();
        formData.append('fileID', value?.fileID);
        formData.append('UploadedFile', files);
        formData.append('FileName', files ? files?.name : value?.fileName);
        formData.append('FileType', files ? files?.type : value?.fileType);
        formData.append('Description', value.description ?? '');
        formData.append('FileSection', 'workOrder');
        formData.append('FileTypeID', !value?.fileTypeID ? '' : value?.fileTypeID === 'n/a' ? '' : value?.fileTypeID);
        formData.append('workOrderID', value.workOrderID);
        formData.append('WorkOrderNumber', value.workOrderNumber);
        formData.append('FileURL', value.fileURL ?? '');
        formData.append(
          'InspectionReportType',
          !value?.fileTypeID ? '' : value?.fileTypeID === 'n/a' ? '' : value?.fileTypeID
        );

        const data = await mutateAsync({ data: formData });
        onSaveComplete(data.data);
        form.reset({ fileUpload: [], description: '', fileTypeID: '' });
      }
    }
  };

  useEffect(() => {
    if (form.watch('fileTypeID')) {
      setMessage(undefined);
    }
  }, [form.watch('fileTypeID')]);

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <ReactHookForm formName="SEVE_FORM" onSubmit={submitHanlder} formContextProps={form}>
      <LoadingIndicator isLoading={isLoading} />
      <Grid container spacing={2}>
        <Grid item lg={!page ? 7 : 5} sm={7} xl={7} xs={7}>
          <div style={styling.dropZone} {...getRootProps()}>
            <input {...getInputProps()} />
            {isDragActive ? (
              <p>Drop the files here ...</p>
            ) : (
              <div>
                {files ? (
                  <p>{`${files.name} are selected to upload`}</p>
                ) : fileData ? (
                  <p>{`${fileData?.fileName} are selected to upload`}</p>
                ) : (
                  <p>Drag and drop a file here, or click to select a file</p>
                )}
              </div>
            )}
          </div>
        </Grid>
        <Grid item lg={!page ? 5 : 4} sm={5} xl={5} xs={5}>
          <FormInput label="Description" name="description" multiline={true} size={4.5} autoFocus />
        </Grid>
        {page && isInspectionReportPermission && (
          <Grid item lg={3} sm={5} xl={5} xs={5}>
            <FormSelect
              label="Inspection Report Type"
              name="fileTypeID"
              options={[
                { value: 'n/a', label: 'N/A' },
                { value: 'Code Report', label: 'Code Report' },
                { value: 'Builder Report', label: 'Builder Report' },
                { value: 'Legacy Report', label: 'Legacy Report' },
                { value: 'Property Not Ready Report', label: 'Property Not Ready Report' },
              ]}
            />
            {message && <span style={{ color: 'red' }}>{message}</span>}
          </Grid>
        )}
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <div className="spaceBetween">
            <Button variant="outlined" style={{ width: '130px' }} onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={(!files && !fileData) || isReadOnlyPermission} type={'submit'} variant="contained" style={{ width: '130px' }}>
              Save
            </Button>
          </div>
        </Grid>
      </Grid>
    </ReactHookForm>
  );
};
